import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    inject,
    OnDestroy,
    OnInit,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import {
    combineLatest,
    debounceTime,
    distinctUntilChanged,
    filter,
    map,
    Observable,
    shareReplay,
    startWith,
    Subscription,
    switchMap,
    take,
    tap,
} from 'rxjs';
import { Store } from '@ngrx/store';

import { TranslateService } from '@ngx-translate/core';
import { isString, pick, valuesIn } from 'lodash';

import { FilterStatic } from 'src/app/components/filter/filter.static';
import {
    FilterFormModel,
    FilterOption,
    QuickFilterOption,
    QUICK_FILTER_FORM_ENTRY
} from 'src/app/models/filter-form.model';
import { capitalizeFirstLetters } from 'src/app/utils/capitalizeFirstLetter';
import { TransformPropertyPipe } from 'src/app/pipes/transform-property.pipe';
import { UserRights } from 'src/app/models/profile';
import { Asset } from 'src/app/models/asset';
import {
    selectAssetReady$,
    selectAssetsArray$,
} from 'src/app/store/selectors/asset.selectors';
import { SignalSources } from 'src/app/models/signal-source';
import {
    selectProfileUserRights$,
    selectProfileUserRightsLoaded$,
} from 'src/app/store/selectors/profile.selectors';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { FilterService } from 'src/app/services/filter.service';
import { MapService } from 'src/app/services/map.service';
import { selectAnchorState$ } from 'src/app/store/selectors/anchor.selectors';
import { EsriBeacon } from 'src/app/models/beacon';
import { Anchor } from 'src/app/models/anchor';
import { selectBeaconLayerState$ } from 'src/app/store/selectors/beacon-layer.selectors';

@Component({
    selector: 'app-quick-search-field',
    templateUrl: './quick-search-field.component.html',
    styleUrls: ['./quick-search-field.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class QuickSearchFieldComponent implements OnInit, OnDestroy {
    public filterAutocompleteLoaders: boolean = false; // To control the visibility of No Options Found message
    public filteredDataForAutocomplete$: Observable<QuickFilterOption[]>; // Contain the result of a value change (partial data because filtered)
    public filterFormEntriesNotFiltered: FilterFormModel[] = QUICK_FILTER_FORM_ENTRY; // Contain the metadata of the component filter
    public filterFormEntries: FilterFormModel[]; // Contain the metadata of the component filter
    public quickSearchForm: FormGroup<{
        searchControl: FormControl<string | QuickFilterOption>;
    }>;
    public assets$: Observable<Asset[]>;
    public mobile: boolean;
    public isClearClicked: boolean;
    public beaconsDisplayed = false;
    public anchorsDisplayed = false;
    public readonly MIN_LENGTH_QUICK_SEARCH = 3;

    private _dataForAutocomplete: QuickFilterOption[] = []; // Autocomplete data at initialisation (full data)
    private _dataSource: FilterOption[] = [];
    private _dataSourceBeacon: QuickFilterOption[] = [];
    private _dataSourceAnchor: QuickFilterOption[] = [];
    private _scope: string[];
    private _userRights$: Observable<UserRights>; // For User right
    private _userRightsLoaded$: Observable<boolean>; // For User right
    private _subscription: Subscription = new Subscription();
    private _isInitialized: boolean = false;
    private _isQuickSearchSelectedLastly: boolean = false;
    private readonly MAX_LENGTH_FILTER_DATA_AUTOCOMPLETE = 200;

    private _store = inject(Store);
    private _translate = inject(TranslateService);
    private _transformPropertyPipe = inject(TransformPropertyPipe);
    private _cd = inject(ChangeDetectorRef);
    private _filterService = inject(FilterService);
    private readonly _breakpointObserver = inject(BreakpointObserver);

    constructor(
        private _mapService: MapService,
    ) { }
    ngOnInit(): void {
        this._userRights$ = this._store.select(selectProfileUserRights$);
        this._userRightsLoaded$ = this._store.select(
            selectProfileUserRightsLoaded$
        );
        this.assets$ = this._store.select(selectAssetReady$).pipe(
            filter((ready) => ready),
            switchMap(() => {
                this._isInitialized = false;
                this.clearInput(true);
                return this._store.select(selectAssetsArray$);
            })
        );

        this._subscription.add(
            this._breakpointObserver
                .observe([Breakpoints.Handset])
                .subscribe(({ matches }) => {
                    this.mobile = matches;
                })
        );

        this._subscription.add(
            this._filterService.hasClickedApplyButton$
                .pipe(debounceTime(500))
                .subscribe(() => {
                    if (!this._isQuickSearchSelectedLastly && this.quickSearchForm) this.clearInput(true);
                    this._isQuickSearchSelectedLastly = false;
                    this.isClearClicked = false;
                    this._cd.markForCheck();
                })
        );
        this._initForm();
        this._initData();
        this._subscription.add(this._mapService.anchorsDisplayed$.subscribe((value) => {
            this.anchorsDisplayed = value;
            if (value) {
                this._store.select(selectAnchorState$).pipe(
                    filter(selectAnchorState$ => selectAnchorState$.loaded),
                    map(selectAnchorState$ => selectAnchorState$.data)
                ).subscribe((anchors) => {
                    for (const anchor of anchors as Anchor[]) {
                        // Check if the asset is available for the map
                        this._dataSourceAnchor.push({
                            key: anchor.id?.toString() ?? '', // or another unique identifier
                            value: anchor.anchorId ?? '', // or another display property,
                            name: anchor.anchorId ?? '', // or another display property,
                            placeholder: 'Anchor',
                            type: 'autoComplete',
                            hierarchyOrder: 0,
                            ...anchor, // spread the rest if needed, or remove if not required
                        });
                    }
                });
            } else {
                this.quickSearchForm.controls['searchControl'].setValue('');
                this._dataSourceAnchor = [];
                this._setAutocompleteList();
            }
        }));
        this._subscription.add(this._mapService.beaconsDisplayed$.subscribe((value) => {
            this.beaconsDisplayed = value;
            if (value) {
                this._store.select(selectBeaconLayerState$).pipe(
                    filter(selectBeaconLayerState$ => selectBeaconLayerState$.loaded),
                    map(selectBeaconLayerState$ => selectBeaconLayerState$.data)
                ).subscribe((beacons) => {
                    for (const beacon of beacons as EsriBeacon[]) {
                        // Check if the asset is available for the map
                        this._dataSourceBeacon.push({
                            key: beacon.attributes.objectid?.toString() ?? '',
                            value: beacon.attributes.name.toString() ?? '',
                            name: beacon.attributes.name.toString() ?? '',
                            placeholder: 'Beacon',
                            hierarchyOrder: 0,
                            type: 'autoComplete',
                            ...beacon,
                        });
                    }
                });
            } else {
                this.quickSearchForm.controls['searchControl'].setValue('');
                this._dataSourceBeacon = [];
                this._setAutocompleteList();
            }

        }));
    }

    /**
     * Invokes the method to set or refresh the autocomplete list.
     */
    public onOpenSelect() {
        this._setAutocompleteList();
    }

    /**
     * Emits the currently selected QuickFilterOption
     * to the `quickFilterApplied$` to subscribe in FilterComponent.
     */
    public selected(): void {
        const selectedOption: any = this.quickSearchForm.controls['searchControl'].value as QuickFilterOption;
        if (selectedOption.placeholder === 'Anchor') {
            this._mapService.zoomAndCenterOnAnchors([{ id: selectedOption.key, anchorId: selectedOption.value, floorLevel: 0, ...selectedOption }])
        } else if (selectedOption.placeholder === 'Beacon') {
            this._mapService.zoomAndCenterOnBeacons([{ ...selectedOption }])
        } else {
            this._filterService.quickFilterApplied$.next(selectedOption);
        }
        this._isQuickSearchSelectedLastly = true;
    }

    /**
     * To display the text in the dropdown option
     */
    public displayFn(option: QuickFilterOption): string {
        return option?.value;
    }

    /**
     * trackBy function for Angular's *ngFor
     */
    public trackByKeyName(index: number, option: QuickFilterOption) {
        return option.key + option.name;
    }

    /**
     * Clears the value of the quickSearchForm.
     * @param [isSubscription=false] if called by subscription `_filterService.clearQuickFilter$.next()` should not be called
     */
    public clearInput(isSubscription = false): void {
        this.isClearClicked = true;
        this.quickSearchForm.controls['searchControl'].setValue('');
        if (!isSubscription) this._filterService.quickFilterClear$.next();
        this._cd.markForCheck();
    }

    /**
     * tranform option value for display purpose
     */
    public transformOption(option: QuickFilterOption): string {
        const placeholder = `<span style='margin-left: auto'>${this._translate.instant(option.placeholder || '')}</span>`;
        if (option.translationRequired) {
            let value = option.value;
            switch (option.translationKeyTransform) {
                case 'lowercase':
                    value = value.toLowerCase();
                    break;
                case 'titlecase':
                    value = capitalizeFirstLetters(value, /[_-\s]/);
                    break;
                case 'uppercase':
                    value = value.toUpperCase();
                    break;
            }
            return `${this._translate.instant(value || '')} ${placeholder}`;
        } else if (option.transformRequired) {
            const lastIndex = option.keyPath.length - 1;
            // get the name of the property based on the key path
            const propertyKey =
                option.keyPath[lastIndex][option.keyPath[lastIndex].length - 1];
            return `${this._transformPropertyPipe.transform(
                option.value,
                propertyKey,
                option.scope
            )} ${placeholder}`;
        } else {
            return `${option.value} ${placeholder}`;
        }
    }

    /**
     * Build the filter form following the filterFormEntry model
     */
    private _initForm(): void {
        this.quickSearchForm = new FormGroup({
            searchControl: new FormControl(null),
        });

        this.filterFormEntries = this.filterFormEntriesNotFiltered.filter(
            (entry) => {
                // If a filter is specific to an app, check if the user already has the right to see it
                if (
                    entry.scope &&
                    !entry.scope.some((val) =>
                        this._scope?.some((auth) => auth === val)
                    )
                ) {
                    return false;
                }
                return true;
            }
        );
    }

    /**
     * Init the data source and autocomplete list
     */
    private _initData() {
        // Check that the user rights are correctly loaded to avoid multiple subscribe calls
        this._subscription.add(
            this._userRightsLoaded$
                .pipe(
                    filter((loaded) => loaded),
                    take(1),
                    switchMap(() =>
                        combineLatest([
                            this.assets$.pipe(shareReplay(1)),
                            this._userRights$.pipe(take(1))
                        ])
                    )
                )
                .subscribe(([dataset, userRights]) => {
                    this._scope = userRights.scope;
                    for (const asset of dataset as Asset[]) {
                        // Check if the asset is available for the map
                        if (this._checkTakeAssetForMap(asset)) {
                            this._dataSource.push({
                                key: asset.id?.toString() ?? '', // or another unique identifier
                                value: asset.name ?? '', // or another display property
                                ...asset, // spread the rest if needed, or remove if not required
                            });
                        }
                    }
                    // Check if the form has already been initialized to avoid multiple useless form re-build
                    if (!this._isInitialized) {
                        this._isInitialized = true;
                        this._setAutocompleteList();
                        this._cd.markForCheck();
                        setTimeout(() => this._prefillForm(), 500);
                    }
                })
        );
        this._listenToSearchControlChanges();
    }

    /**
     * Handles value changes for the search input and updates autocomplete options
     */
    private _listenToSearchControlChanges() {
        this.filteredDataForAutocomplete$ = this.quickSearchForm.controls[
            'searchControl'
        ].valueChanges.pipe(
            tap(() => {
                this.filterAutocompleteLoaders = true;
            }),
            startWith(''),
            debounceTime(300),
            distinctUntilChanged(),
            map((value) => {
                const valueString = isString(value)
                    ? value
                    : (value as QuickFilterOption)?.value;
                if (valueString?.length < this.MIN_LENGTH_QUICK_SEARCH) {
                    return [];
                }
                return this._filterDataForAutocomplete(
                    valueString,
                    this._dataForAutocomplete
                );
            })
        );
    }

    /**
     * Check if the asset has to be selected for the filters on the map related components
     * @param asset The asset to check
     */
    private _checkTakeAssetForMap(asset: Asset): boolean {
        // Check last event has at least key
        if (!Object.keys(asset?.last_event ?? {}).length) {
            return false;
        }
        // Check that these properties have all a truthy value
        const propertiesNeedsTruthy = pick(asset.last_event, [
            'source',
            'lng',
            'lat',
            'event_time',
        ]);
        if (valuesIn(propertiesNeedsTruthy).some((value) => !value)) {
            return false;
        }
        /*
            Check for dual-tag 
            Tracker is not defined, but the asset is taken into account
        */
        if (propertiesNeedsTruthy.source === SignalSources.GO2SCAN) {
            return true;
        }
        const { tracker } = asset;
        // Tracker needs to be defined
        return !!Object.keys(tracker ?? {}).length;
    }

    /**
     * Set the autocomplete list (select options) for the all the entries
     * @param entry Filter form model containing all properties
     */
    private _setAutocompleteList() {
        const dataForAutocompleteTemp = [];
        for (const entry of this.filterFormEntries) {
            const dataForAutocompleteForEachEntries =
                FilterStatic.getFilterOptions(
                    entry,
                    this._dataSource,
                    this.filterFormEntriesNotFiltered,
                    {}
                ).map((each) => ({
                    ...each,
                    ...entry
                }));
            dataForAutocompleteTemp.push(...dataForAutocompleteForEachEntries);
        }
        this._dataForAutocomplete = dataForAutocompleteTemp;
        this._dataForAutocomplete = [...this._dataForAutocomplete, ...this._dataSourceBeacon, ...this._dataSourceAnchor];
        this.filterAutocompleteLoaders = false;
    }

    /**
     * Filter the data in the autocomplete drop down list with the value entered by the user
     * @param value Value entered by the user. Could be a String or a string[] depending if its initialisation/autocomplete value, or if there is a selected filter
     * @param entryName Entry name filter of auto complete
     */
    private _filterDataForAutocomplete(
        value: string,
        // entry: FilterFormModel,
        data: QuickFilterOption[]
    ): QuickFilterOption[] {
        const filterValue = !value ? '' : value.toString().toLowerCase();
        // Only authorized 200 values max to display on the list
        const filteredOptions = data
            .filter((datum) =>
                datum?.value.toString().toLowerCase().includes(filterValue)
            )
            .sort((a, b) => a.value.localeCompare(b.value))
            .slice(0, this.MAX_LENGTH_FILTER_DATA_AUTOCOMPLETE);
        this.filterAutocompleteLoaders = false;
        return filteredOptions;
    }


    /**
     * Subscribes to the `quickFilterPrefillQueryParams$` observable from the filter service
     * and pre-fills the quick search form control with the corresponding autocomplete data
     * when a valid key is emitted.
     */
    private _prefillForm() {
        this._subscription.add(
            this._filterService.quickFilterPrefillQueryParams$.subscribe(key => {
                if (key) {
                    const filtered = this._dataForAutocomplete.find(each => each.key === key);
                    this.quickSearchForm.controls['searchControl'].setValue(filtered);
                }
            })
        );
    }

    ngOnDestroy() {
        this._subscription.unsubscribe();
    }
}
